import { FormControlCheckbox } from '@components/utility/checkboxes';
import { alpha, TextField, Typography } from '@mui/material';
import clsx from 'clsx';
import { ChangeEvent, forwardRef, ReactNode, useCallback, useImperativeHandle, useState } from 'react';

const FIELDS_ORDER: Record<number, string> = {
    0: 'email',
    1: 'first name',
    2: 'last name',
    3: 'company',
};

interface Props {
    listState: [string, React.Dispatch<React.SetStateAction<string>>];
    errorMessageState: [string, React.Dispatch<React.SetStateAction<string>>];
    allowAdvancedMode?: boolean;
    hintText?: ReactNode;
    advancedModeHintText?: ReactNode;
    fieldLabel?: ReactNode;
    minRows?: number | 'auto';
}

export interface ImportCSVListRef {
    advancedMode: boolean;
}

const ImportCSVList = forwardRef<ImportCSVListRef, Props>(
    (
        {
            listState: [list, setList],
            errorMessageState: [errorMessage, setErrorMessage],
            allowAdvancedMode = true,
            hintText = 'List must contain the email address. Please add one guest per line.',
            advancedModeHintText = "List must contain the email address, first name, last name and the company name, separated by a ',' (comma). Please add one member per line",
            fieldLabel = 'Add guests',
            minRows = 10,
        },
        ref
    ) => {
        const [advancedMode, setAdvancedMode] = useState(false);
        const shouldFillHeight = minRows === 'auto';

        useImperativeHandle(ref, () => ({
            advancedMode,
        }));

        const handleChangeAndValidateInput = useCallback(
            (e: ChangeEvent<HTMLTextAreaElement>, advancedMode?: boolean) => {
                const inputValue = e.target.value;
                setList(inputValue);
                const lines = inputValue.split('\n');
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                const errors: string[] = [];

                lines.forEach((line, index) => {
                    const fields = line.split(',');

                    if (advancedMode) {
                        if (fields.length !== 4) {
                            errors.push(`Line ${index + 1} does not contain exactly 4 fields`);
                        } else {
                            const trimmedFields = fields.map((field) => field.trim());
                            for (const [index, field] of trimmedFields.entries()) {
                                if (!field) {
                                    errors.push(`Line ${index + 1} has missing ${FIELDS_ORDER[index]}`);
                                }
                            }
                            const [email] = trimmedFields;
                            if (!emailRegex.test(email)) {
                                errors.push(`Invalid email format on line ${index + 1}`);
                            }
                        }
                    } else {
                        if (fields.length !== 1) {
                            errors.push(`Line ${index + 1} should contain only the email address`);
                        } else {
                            const email = fields[0].trim();
                            if (!email) {
                                errors.push(`Line ${index + 1} has an empty email field`);
                            } else if (!emailRegex.test(email)) {
                                errors.push(`Invalid email format on line ${index + 1}`);
                            }
                        }
                    }
                });

                if (errors.length > 0) {
                    setErrorMessage(errors.join('\n'));
                } else {
                    setErrorMessage('');
                }
            },
            [setErrorMessage, setList]
        );

        const handleAdvancedModeChange = useCallback(() => {
            setAdvancedMode(!advancedMode);
            handleChangeAndValidateInput(
                { target: { value: list } } as ChangeEvent<HTMLTextAreaElement>,
                !advancedMode
            );
        }, [advancedMode, handleChangeAndValidateInput, list]);

        return (
            <div className={clsx({ 'flex h-full min-h-0 flex-col': shouldFillHeight })}>
                <TextField
                    multiline
                    minRows={shouldFillHeight ? undefined : minRows}
                    variant="outlined"
                    fullWidth
                    size="small"
                    label={fieldLabel}
                    onChange={(e) => handleChangeAndValidateInput(e as ChangeEvent<HTMLTextAreaElement>, advancedMode)}
                    value={list}
                    error={!!errorMessage}
                    helperText={!!errorMessage ? <pre className="font-roboto">{errorMessage}</pre> : ''}
                    className={clsx('rounded-lg', { 'flex-1 min-h-0': shouldFillHeight })}
                    sx={
                        shouldFillHeight
                            ? {
                                  '& .MuiInputBase-root': {
                                      height: '100%',
                                      alignItems: 'stretch',
                                  },
                                  '& .MuiInputBase-inputMultiline': {
                                      height: '100% !important',
                                      overflow: 'auto !important',
                                  },
                              }
                            : undefined
                    }
                    slotProps={{
                        input: {
                            classes: {
                                formControl: 'shadow',
                            },
                        },
                    }}
                />
                <div className="mt-3 mb-7">
                    <Typography fontFamily="Roboto" fontSize={12} letterSpacing={0.4} color={alpha('#000', 0.6)}>
                        {advancedMode ? advancedModeHintText : hintText}
                    </Typography>
                    {advancedMode ? (
                        <Typography
                            fontFamily="Roboto"
                            fontSize={12}
                            letterSpacing={0.4}
                            color={alpha('#000', 0.6)}
                            marginTop={3}
                        >
                            Example: linda@email.com, Linda, Watson, Watson Inc.
                        </Typography>
                    ) : null}
                </div>
                {allowAdvancedMode ? (
                    <FormControlCheckbox
                        className="p-0"
                        checked={advancedMode}
                        onChange={handleAdvancedModeChange}
                        labelProps={{ label: 'Advanced Mode' }}
                    />
                ) : null}
            </div>
        );
    }
);

export { ImportCSVList };
